import { times } from 'lodash';
import numbro from 'numbro';
import * as yup from 'yup';
import { OptionType } from '../../../../../../CommonComponents/OPL/Select/BaseSelect/types';
import { hypotheticalReturnValidation } from '../../../../../../CommonValidations/hypoteticalReturn';
import { AccountBeneficiariesParams } from '../../../../api/useAccountBeneficiariesMutation';
import { AccountPortfolioBody } from '../../../../api/useAccountPortfolioMutation';
import {
  AccountPortfolioDTO,
  AssetAllocationsByTaxStatusDTO,
} from '../../../../api/useAccountPortfolioQuery';
import {
  AccountType,
  AssetAllocationDTO,
  BeneficiaryOptionDTO,
  DistributionStrategyTypeDTO,
  OptionsDTO,
  OwnerOptionDTO,
} from '../../../../api/useAccountsOptionsQuery';
import {
  AccountBeneficiariesDTO,
  AccountDTO,
  AccountEarmarkDTO,
  AccountHoldingDTO,
  AccountTaxStatusDTO,
  AllocationTaxStatusDTO,
  AnnuityLivingBenefitRiderDTO,
  BalanceTaxStatusOptionDTO,
  BalanceUnitDTO,
  BeneficiaryDTO,
  ContributionPeriodDTO,
  ContributionsDTO,
  ContributionTaxStatusDTO,
  ContributionUnitDTO,
  DAFScheduleDTO,
  DistributionStrategyDTO,
  EmployerMultiTierMatchDTO,
  MortgageTypeEnum,
  OwnerModelDTO,
  OwnerTypeDTO,
  TaxStatusDTO,
} from '../../../../api/useAccountsQuery';

import { BeneficiaryType } from '../../../../../../ProfileOPL/PersonalInfo/api/useTrustsQuery';
import { EXCLUDE_FROM_ASSET_BUCKETING_OPTION_VALUE } from './components/AssetBucketing/AssetBucketing.meta';
import { getDefaultAssetAllocation } from '../../../../helpers';

const IRA_INHERITED_YEAR_MAX = 100;
const CURRENT_YEAR = new Date().getFullYear();

export const YEAR_INHERITED_OPTIONS = times(IRA_INHERITED_YEAR_MAX + 1).map(offset => ({
  label: `${CURRENT_YEAR - offset}`,
  value: CURRENT_YEAR - offset,
}));

export const UNIT_OPTIONS = [
  { label: '$', value: BalanceUnitDTO.Currency },
  { label: '%', value: BalanceUnitDTO.Percentage },
];

export const DEFAULT_RETIREMENT_ACCOUNTS = [
  AccountType.IRA,
  AccountType.Roth,
  AccountType.SepIra,
  AccountType.InheritedIra,
  AccountType.InheritedRoth,
  AccountType.SimpleIra,
  AccountType.EmployerPlan,
  AccountType.E401K,
  AccountType.Roth401K,
  AccountType.E403B,
  AccountType.E401A,
  AccountType.E457,
];

export const JOINT_ACCOUNTS_LIST = [
  AccountType.Checking,
  AccountType.CD,
  AccountType.Savings,
  AccountType.Brokerage,
  AccountType.CreditCard,
  AccountType.Installment,
  AccountType.LineOfCredit,
  AccountType.AutoLoan,
];

export const ACCOUNTS_WITHOUT_TRUSTS_LIST = [
  AccountType.SepIra,
  AccountType.SimpleIra,
  AccountType.EmployerPlan,
  AccountType.E401K,
  AccountType.Roth401K,
  AccountType.E401A,
  AccountType.E403B,
  AccountType.E457,
  AccountType.TaxFree,
];

export const ACCOUNTS_WITH_HOLDINGS_TAB = [
  AccountType.Brokerage,
  AccountType.IRA,
  AccountType.Roth,
  AccountType.InheritedIra,
  AccountType.InheritedRoth,
  AccountType.TaxFree,
  AccountType.Annuity,
  AccountType.E529,
  AccountType.SepIra,
  AccountType.SimpleIra,
  AccountType.EmployerPlan,
  AccountType.E401K,
  AccountType.Roth401K,
  AccountType.E403B,
  AccountType.E401A,
  AccountType.E457,
  AccountType.HSA,
  AccountType.TaxDeferred,
  AccountType.DonorAdvisedFund,
];

export const ACCOUNTS_WITH_BENEFICIARIES_TAB = [
  AccountType.Brokerage,
  AccountType.Checking,
  AccountType.CD,
  AccountType.Annuity,
  AccountType.E529,
  AccountType.EmployerPlan,
  AccountType.E401K,
  AccountType.Roth401K,
  AccountType.E401A,
  AccountType.E403B,
  AccountType.E457,
  AccountType.InheritedIra,
  AccountType.InheritedRoth,
  AccountType.Savings,
  AccountType.SepIra,
  AccountType.SimpleIra,
  AccountType.TaxFree,
  AccountType.IRA,
  AccountType.Roth,
  AccountType.HSA,
  AccountType.TaxDeferred,
  AccountType.DonorAdvisedFund,
];

export const ACCOUNTS_WITH_ASSET_ALLOCATION_TAB = [
  AccountType.Annuity,
  AccountType.Brokerage,
  AccountType.E529,
  AccountType.EmployerPlan,
  AccountType.E401K,
  AccountType.Roth401K,
  AccountType.E401A,
  AccountType.E403B,
  AccountType.E457,
  AccountType.InheritedIra,
  AccountType.InheritedRoth,
  AccountType.SepIra,
  AccountType.SimpleIra,
  AccountType.TaxFree,
  AccountType.IRA,
  AccountType.Roth,
  AccountType.HSA,
  AccountType.TaxDeferred,
  AccountType.DonorAdvisedFund,
];

export const ROTH_ACCOUNT_TYPES = [
  AccountType.E401K,
  AccountType.E403B,
  AccountType.SepIra,
  AccountType.SimpleIra,
];

interface AccountOwner {
  label: string;
  value: string;
}

export enum AnnuityType {
  Fixed,
  Variable,
  Indexed,
}

export const getAccountHasAssetAllocationTab = (
  accountType: AccountType | null,
  context: { annuityType?: AnnuityType | null },
): boolean => {
  if (typeof accountType !== 'number') {
    return false;
  }

  if (accountType === AccountType.Annuity) {
    return (
      context.annuityType === AnnuityType.Variable || context.annuityType === AnnuityType.Indexed
    );
  }

  return ACCOUNTS_WITH_ASSET_ALLOCATION_TAB.includes(accountType);
};

interface AccountOwner {
  label: string;
  value: string;
}

export const ANNUITY_TYPE_OPTIONS = [
  { label: 'Fixed Annuity', value: AnnuityType.Fixed },
  { label: 'Variable Annuity', value: AnnuityType.Variable },
  { label: 'Indexed Annuity', value: AnnuityType.Indexed },
];

export const ANNUITY_LIVING_BENEFIT_OPTIONS = [
  { label: 'None', value: false },
  { label: 'Included', value: true },
];

export const ANNUITY_DEATH_BENEFIT_OPTIONS = [
  { label: 'None', value: false },
  { label: 'Included', value: true },
];

interface Contributions
  extends Omit<ContributionsDTO, 'PersonalAmount' | 'EmployerMultiTierMatch'> {
  PersonalAmount: string | number;
  EmployerMultiTierMatch: EmployerMultiTierMatchDTO<string>[] | null;
}

type BalanceTaxStatus = {
  Amount?: number;
  Earmarks?: OptionType<string>[];
  NonSpecificPositionsValue: number | string;
  AssetBucketIndex?: number | null;
};

export interface Position extends Omit<AccountHoldingDTO, 'Quantity' | 'Price' | 'MarketValue'> {
  AssetAllocation: AssetAllocationDTO[];
  Quantity: number | null;
  Price: number | null;
  MarketValue: number;
}

export interface Beneficiaries extends Omit<BeneficiaryDTO, 'Percentage' | 'Id'> {
  Percentage: string;
  Id: string | null;
}

interface AccountBeneficiaries extends Omit<AccountBeneficiariesDTO, 'Beneficiaries'> {
  Beneficiaries: Beneficiaries[];
}

interface OwnerModelFormDTO {
  Id: string | null;
  Name: string;
  OwnerType: OwnerTypeDTO;
  Percentage: string;
}

interface BankAccountFormFields {
  AccountId?: string;
  AccountType: AccountType | null;
  InstitutionName: string | null;
  InstitutionId: string | null;
  AccountOwner: AccountOwner;
  AccountName: string | null;
  AccountNickname: string | null;
  NonSpecificPositionsValue: string | number | null;
  AccountEarmark: OptionType<string>[];
  IsBrokerOfRecord: boolean;
  HasAdvisorChangedBrokerOfRecord: boolean;
  CustomAssetBucketIndex: number | null;
  ContributionStartYear: OptionType<number | null> | null;
  ContributionEndYear: OptionType<number | null> | null;
  AccountBeneficiaries: AccountBeneficiaries | null;
  DistributionStrategyType: DistributionStrategyTypeDTO | null;
  DistributionStrategy: DistributionStrategyDTO | null;
  CustomDistributionStrategy?: any;
  EmployerNonElectiveContribution: number | null;
  EmployerNonElectiveContributionTaxStatus: number | null;
  EmployerMatchUnits: ContributionUnitDTO | null;
  SelfEmployed: boolean;
  IsSpouseSoleBeneficiary: boolean;
  MaximizeContributions: boolean;
  YearInherited: number | null;
  DistributionCalculationMethod: number;
  IraDistributionMethod: number;
  IraDistributionStartYear: number | null;
  IraDistributionSchedule: string;
  AnnuitizationDistributionDuration: number | null;
  AnnuitizationStartAtRetirement: boolean | null;
  AnnuitizationStartYear: number | null;
  AnnuityAnnualReturn: number | null;
  AnnuityDeathBenefitRiderAmount: number | null;
  AnnuityDistributionOption: number | null;
  AnnuityHasDeathBenefitRider: boolean | null;
  AnnuityHasLivingBenefitRider: boolean | null;
  AnnuityIndexName: string | null;
  AnnuityIsAlreadyAnnuitized: boolean | null;
  AnnuityIsAnnuitized: boolean | null;
  AnnuityIsQualified: boolean | null;
  AnnuityLivingBenefitRider: AnnuityLivingBenefitRiderDTO | null;
  AnnuityParticipationRate: number | null;
  AnnuityPretaxPrincipal: boolean | null;
  AnnuityPrincipal: number | null;
  AnnuityPurchaseYear: number | null;
  AnnuitySurrenderSchedule: string | null;
  AnnuitySurvivorBenefit: number | null;
  AnnuityType: number | null;
  AnnualReturnFloor: number | null;
  AnnualReturnCap: number | null;

  IsLiquid: boolean;
  OriginationDate: string | null;
  MonthlyPmi: number | null;
  MortgageType: MortgageTypeEnum | null;
  AssociatedAssetId: string | null;
  IsLinked: boolean;
  IsOrion: boolean;
  IsIntegrated: boolean;
  HasAsset: boolean;
  HasPositions: boolean;
  ShowSyncMonthlyContributionsToggle: boolean;
  IsMoveAllowed: boolean;
  ShowAccount: boolean;
  SyncAccountValueEnabled: boolean;
  SyncAssetAllocationEnabled: boolean;
  SyncMonthlyContributionEnabled: boolean;
  SyncTransactionsEnabled: boolean;
  SyncPositionsEnabled: boolean;
  SyncCostBasisEnabled: boolean;
  SyncSourceLabel: string | null;
  IncludeInGrossEstate: boolean;
  Contributions: Contributions[];
  TaxStatus: AccountTaxStatusDTO | null;
  Positions: Position[];
  Balances?: {
    PreTax?: BalanceTaxStatus;
    Roth?: BalanceTaxStatus;
    AfterTax?: BalanceTaxStatus;
    Unit?: BalanceUnitDTO;
  };
  SpecificPositionsValue: number;
  OrionManagementStyleId: number | null;
  OwnerModel: OwnerModelFormDTO[];
  IsOrionManagedAccount?: boolean;
  MonthlyDistributionAmount: number | null;
  TurnoverRatio: string | number | null;
  CostBasis: string | number | null;
  CapitalGainsRatio: number | string | null;
  PercentageUsedInPlan: number | null;
  ExcludeFromDebtManagementGoal: boolean;
  FundingSourceAccountId: string | null;
  DAFContributionSchedule: DAFScheduleDTO;
  DAFGrantSchedule: DAFScheduleDTO;
  IsBypassRothIraIncomeLimits: boolean;
  StudentLoanOriginationDate: string | null;
}

export interface BankAccountBody
  extends Omit<
    BankAccountFormFields,
    | 'AccountEarmark'
    | 'AccountOwner'
    | 'ContributionEndYear'
    | 'ContributionStartYear'
    | 'EmployerMatchType'
    | 'EmployerLimitType'
    | 'EmployerMatchUnits'
    | 'IraDistributionMethod'
    | 'DistributionCalculationMethod'
    | 'IraDistributionStartYear'
    | 'YearInherited'
    | 'AnnuityType'
    | 'TaxStatus'
    | 'OwnerModel'
  > {
  AccountOwner: string;
  AccountEarmark: AccountEarmarkDTO[];
  'AccountEarmark[Type]': string[];
  ContributionEndYear: number | null;
  ContributionStartYear: number | null;
  IraDistributionMethod: number | null;
  DistributionCalculationMethod: number | null;
  IraDistributionStartYear: number | null;
  YearInherited: number | null;
  AnnuityType: AnnuityType | null;
  TaxStatus?: AccountTaxStatusDTO;
  OwnerModel: OwnerModelDTO[];
}

interface AssetAllocation extends Omit<AssetAllocationDTO, 'y'> {
  y: string | number;
}

export interface AssetAllocationList {
  taxStatus: AllocationTaxStatusDTO | null;
  assetAllocation: AssetAllocation[];
  hypotheticalReturn?: string | number | null;
}

interface AccountPortfolioFormFields {
  assetAllocationList: AssetAllocationList[];
}

export interface FormFields {
  bankAccount: BankAccountFormFields;
  accountPortfolio: AccountPortfolioFormFields | null;
}

export interface LocalSaveOptionsData {
  bankAccount: any;
  accountPortfolio: any;
}

export interface LocalSaveOptions {
  data: LocalSaveOptionsData | null;
  onSave(data: LocalSaveOptionsData): void;
  showHypotheticalReturn?: boolean;
}

export const ACCOUNTS_WITH_DEFAULT_PERSONAL_UNIT_CONTRIBUTION: AccountType[] = [
  AccountType.SepIra,
  AccountType.SimpleIra,
];

export const ACCOUNTS_WITH_DEFAULT_ANNUAL_CONTRIBUTION: AccountType[] = [
  AccountType.SepIra,
  AccountType.SimpleIra,
  AccountType.TaxDeferred,
];

export const DEFAULT_CONTRIBUTIONS = {
  EmployerEmployerLimit: null,
  EmployerLimitType: null,
  EmployerMatchAmount: null,
  EmployerMatchPercent: null,
  EmployerMatchType: null,
  EmployerMultiTierMatch: null,
  EmployerTaxStatus: ContributionTaxStatusDTO.Traditional,
  PersonalAmount: '',
  PersonalFrequency: ContributionPeriodDTO.Monthly,
  PersonalMaximize: false,
  PersonalTaxStatus: ContributionTaxStatusDTO.Traditional,
  PersonalUnit: BalanceUnitDTO.Currency,
  EmployerFrequency: ContributionPeriodDTO.Monthly,
  EmployerUnit: BalanceUnitDTO.Currency,
  EmployerAmount: '',
};

export const getDefaultContribution = (accountType: AccountType | null = null) => {
  if (accountType !== null) {
    return {
      ...DEFAULT_CONTRIBUTIONS,
      PersonalFrequency: ACCOUNTS_WITH_DEFAULT_ANNUAL_CONTRIBUTION.includes(accountType)
        ? ContributionPeriodDTO.Annually
        : ContributionPeriodDTO.Monthly,
      PersonalUnit: ACCOUNTS_WITH_DEFAULT_PERSONAL_UNIT_CONTRIBUTION.includes(accountType)
        ? BalanceUnitDTO.Percentage
        : BalanceUnitDTO.Currency,
    };
  }

  return DEFAULT_CONTRIBUTIONS;
};

// Initial values are only bank account values.
// Assets and Beneficiaries are added later.
export const dataToForm = (data: AccountDTO, options: OptionsDTO): BankAccountFormFields => {
  const { Balances, Contributions, AccountBeneficiaries, ...rest } = data;

  const getCustomAssetBucketIndex = (baseFormKey?: BalanceTaxStatusOptionDTO) => {
    const assetBucketIndex =
      baseFormKey && data.Balances
        ? data.Balances[baseFormKey]?.AssetBucketIndex ?? null
        : data.CustomAssetBucketIndex;

    if (
      options.AssetBucketingByManagementStyle &&
      data.OrionManagementStyleId !== null &&
      assetBucketIndex === null
    ) {
      const selectedManagementStyles = options.ManagementStyles.find(
        option => option.id === data.OrionManagementStyleId,
      );

      return selectedManagementStyles?.defaultBucket ?? null;
    }

    if (!options.AssetBucketingByManagementStyle && assetBucketIndex === null) {
      return (
        options.DefaultAssetBucketingIndices[AccountType[data.AccountType]] ??
        EXCLUDE_FROM_ASSET_BUCKETING_OPTION_VALUE
      );
    }

    return assetBucketIndex;
  };

  return {
    ...rest,

    AccountBeneficiaries: AccountBeneficiaries
      ? {
          ...AccountBeneficiaries,
          Beneficiaries: AccountBeneficiaries.Beneficiaries.map(beneficiary => ({
            ...beneficiary,
            Percentage: beneficiary.Percentage.toString(),
          })),
        }
      : AccountBeneficiaries,

    AccountEarmark: (data.AccountEarmark || []).map(({ Tooltip, Type }) => ({
      label: Tooltip,
      value: Type,
    })),
    AccountOwner: { label: data.AccountOwner, value: data.AccountOwner },
    OwnerModel: data.OwnerModel.map(owner => ({
      ...owner,
      Percentage: (owner.Percentage * 100).toFixed(2),
    })),
    ContributionStartYear:
      options.ContributionStartYearOptions.find(
        ({ value }) => value === data.ContributionStartYear,
      ) || null,
    ContributionEndYear:
      options.ContributionEndYearOptions.find(({ value }) => value === data.ContributionEndYear) ||
      null,

    Contributions:
      Contributions.length > 0 ? Contributions : [getDefaultContribution(data.AccountType)],

    DistributionStrategyType:
      options.CustomDistributionStrategyOptions.distributionStrategyType.find(
        ({ value }) => value === data.DistributionStrategyType,
      )?.value ?? DistributionStrategyTypeDTO.None,
    EmployerMatchUnits: data.EmployerMatchUnits ?? null,
    DistributionStrategy: data.DistributionStrategy
      ? {
          ...data.DistributionStrategy,
          ComputationYearType: options.CustomDistributionStrategyOptions.computationYearType.find(
            ({ value }) => value === data.DistributionStrategy.ComputationYearType,
          )?.value,
        }
      : null,
    AnnuityType: data.AnnuityType,
    TaxStatus: data.TaxStatus ?? null,
    Positions: (data.Positions || []).map(position => {
      const defaultAssetAllocation = getDefaultAssetAllocation(
        options.DefaultAssetAllocation,
        data,
      );

      if (!defaultAssetAllocation) {
        return { ...position, AssetAllocation: [] };
      }

      return {
        ...position,
        IsPriceManuallyEntered: position.IsPriceManuallyEntered ?? false,
        AssetBucketIndex: options.AssetBucketingByManagementStyle
          ? null
          : position.AssetBucketIndex,
        AssetAllocation: defaultAssetAllocation.map(asset => {
          const foundAllocation = position.AssetClasses ? position.AssetClasses[asset.id] : null;

          return {
            ...asset,
            y: foundAllocation ?? 0,
          };
        }),
      };
    }),
    ...(Balances
      ? {
          Balances: {
            ...(Object.keys(Balances).filter(
              key => key !== 'Unit',
            ) as BalanceTaxStatusOptionDTO[]).reduce((acc, key) => {
              if (Balances[key]) {
                return {
                  ...acc,
                  [key]: {
                    ...Balances[key],
                    AssetBucketIndex: getCustomAssetBucketIndex(key),
                    Earmarks: (Balances[key]?.Earmarks || []).map(({ Tooltip, Type }) => ({
                      label: Tooltip,
                      value: Type,
                    })) as OptionType<string>[],
                  },
                };
              }

              return acc;
            }, {} as BalanceTaxStatus),

            Unit: data.Balances?.Unit === undefined ? BalanceUnitDTO.Currency : data.Balances.Unit,
          },
        }
      : {}),

    AnnuitySurrenderSchedule: data.AnnuitySurrenderSchedule || null,
    ...(data.AnnuityLivingBenefitRider
      ? {
          AnnuityLivingBenefitRider: {
            ...data.AnnuityLivingBenefitRider,
            IncomeStartAtRetirement: data.AnnuityLivingBenefitRider?.IncomeStartYear === -1,
          },
        }
      : {}),

    CustomAssetBucketIndex: getCustomAssetBucketIndex(),
  };
};

export const bankAccountsFormToBody = (bankAccount: BankAccountFormFields): BankAccountBody => {
  const { Balances, TaxStatus, AccountBeneficiaries, ...formValues } = bankAccount;

  return {
    ...formValues,

    AccountBeneficiaries:
      AccountBeneficiaries && AccountBeneficiaries.Beneficiaries.length > 0
        ? {
            ...AccountBeneficiaries,
            Beneficiaries: AccountBeneficiaries.Beneficiaries.filter(
              beneficiary => beneficiary.Name,
            ),
          }
        : null,

    AccountEarmark: bankAccount.AccountEarmark.map(({ value, label }) => ({
      Tooltip: label,
      Type: value,
    })),
    'AccountEarmark[Type]': bankAccount.AccountEarmark.map(({ label }) => label),
    AccountOwner: bankAccount.AccountOwner.label,
    OwnerModel: bankAccount.OwnerModel.filter(b => b.Name && b.Percentage).map(owner => ({
      ...owner,
      Percentage: Number((parseFloat(owner.Percentage) / 100).toFixed(4)),
    })),
    ContributionStartYear: bankAccount.ContributionStartYear?.value || null,
    ContributionEndYear: bankAccount.ContributionEndYear?.value || null,
    DistributionStrategyType: bankAccount.DistributionStrategyType ?? null,
    DistributionStrategy: {
      ...bankAccount.DistributionStrategy,
      ComputationYearType: bankAccount.DistributionStrategy?.ComputationYearType ?? 0,
    },
    IraDistributionMethod: bankAccount.IraDistributionMethod ?? 0,
    DistributionCalculationMethod: bankAccount.DistributionCalculationMethod ?? 0,
    IraDistributionStartYear: bankAccount.IraDistributionStartYear || null,
    YearInherited: bankAccount.YearInherited || null,

    ...(TaxStatus !== null ? { TaxStatus } : {}),

    ...(Balances
      ? {
          Balances: {
            ...Object.keys(Balances).reduce((acc, key) => {
              if (key === 'Unit') {
                return acc;
              }

              return {
                ...acc,
                [key]: {
                  ...acc[key],
                  ...Balances[key],
                  Earmarks: (Balances[key].Earmarks || []).map(({ label, value }) => ({
                    Tooltip: label,
                    Type: value,
                  })),
                },
              };
            }, {}),
            Unit: Balances.Unit,
          },
        }
      : {}),

    Positions: formValues.AnnuityType === AnnuityType.Fixed ? [] : formValues.Positions,
    SpecificPositionsValue:
      formValues.AnnuityType === AnnuityType.Fixed ? 0 : formValues.SpecificPositionsValue,
  };
};

export const accountPortfolioFormToBody = (
  accountPortfolio: AccountPortfolioFormFields | null,
): Omit<AccountPortfolioBody, 'AccountId' | 'clientId'> | null => {
  if (!accountPortfolio) {
    return null;
  }

  return {
    AccountPortfolioByTaxStatuses: accountPortfolio.assetAllocationList.map(assetAllocation => ({
      TaxStatus: assetAllocation.taxStatus,
      HypotheticalReturn: assetAllocation.hypotheticalReturn || null,
      AssetAllocation: assetAllocation.assetAllocation.map(allocation => {
        return {
          ...allocation,
          y: allocation.y === '' ? 0 : numbro(allocation.y).value(),
        };
      }),
    })),
  };
};

export const bankAccountBeneficiariesFormToBeneficiariesBody = (
  accountBeneficiaries: AccountBeneficiaries,
  beneficiariesOptions: BeneficiaryOptionDTO[],
): Omit<AccountBeneficiariesParams, 'clientId' | 'AccountId'> => {
  return {
    ...accountBeneficiaries,
    Beneficiaries: accountBeneficiaries.Beneficiaries.filter(b => b.Name).map(b => ({
      ...b,
      ...(beneficiariesOptions.find(opt => opt.Id === b.Id) as BeneficiaryDTO),
    })),
  };
};

interface AccountPortfolioDataToFormParams {
  accountType?: number;
  accountPortfolio?: AccountPortfolioDTO;
  accountOptions: OptionsDTO;
}

export const accountPortfolioDataToForm = ({
  accountType,
  accountPortfolio,
  accountOptions,
}: AccountPortfolioDataToFormParams) => {
  if (!accountType || !accountOptions) {
    return undefined;
  }

  const { AccountTypeOptions } = accountOptions;

  const accountTypeOptions = AccountTypeOptions.find(
    options => accountType === options.AccountType,
  );

  const defaultAssetAllocation = getDefaultAssetAllocation(accountOptions.DefaultAssetAllocation, {
    AccountType: accountType,
    TaxStatus: TaxStatusDTO.PreTax,
    AnnuityIsAnnuitized: null,
  });

  const assetAllocationSettings = accountPortfolio?.AssetAllocation || defaultAssetAllocation;

  if (!assetAllocationSettings || !accountTypeOptions) {
    return undefined;
  }

  const { AssetAllocationTaxStatuses } = accountTypeOptions;

  const defaultAssetAllocationByTaxStatus = AssetAllocationTaxStatuses.reduce(
    (acc, { Value: key }) => {
      return {
        ...acc,
        [key]: {
          AssetAllocation: defaultAssetAllocation,
        },
      };
    },
    {} as AssetAllocationsByTaxStatusDTO,
  );

  return {
    assetAllocationList: [
      {
        assetAllocation: (accountPortfolio?.AssetAllocation || defaultAssetAllocation).map(
          asset => {
            return {
              ...asset,
              y: numbro(asset.y)
                .multiply(100)
                .value(),
            };
          },
        ),
        taxStatus: null,
        hypotheticalReturn: accountPortfolio?.HypotheticalReturn,
      },
      ...Object.entries(
        accountPortfolio?.AssetAllocationsByTaxStatus || defaultAssetAllocationByTaxStatus,
      ).map(([key, value]) => ({
        taxStatus: +key,
        assetAllocation: value.AssetAllocation.map(asset => ({ ...asset, y: asset.y * 100 })),
      })),
    ],
  };
};

export const EMPLOYER_MATCH_ACCOUNT_TYPES: AccountType[] = [
  AccountType.E401K,
  AccountType.Roth401K,
  AccountType.E401A,
  AccountType.E403B,
  AccountType.E457,
  AccountType.TaxDeferred,
  AccountType.TaxFree,
  AccountType.SimpleIra,
];

export const defaultFormValues = {
  AccountType: null,
  InstitutionName: null,
  InstitutionId: null,
  AccountName: null,
  AccountNickname: null,
  AccountOwner: null,
  NonSpecificPositionsValue: null,
  AccountEarmark: [],
  ContributionAmount: null,
  ContributionPercent: null,
  ContributionPeriod: ContributionPeriodDTO.Monthly,
  IsBrokerOfRecord: false,
  CustomAssetBucketIndex: null,
  AccountBeneficiaries: {
    Beneficiaries: [],
    LastBeneficiaryUpdate: null,
  },
  DistributionStrategyType: DistributionStrategyTypeDTO.None,
  OriginationDate: null,
  MonthlyPmi: null,
  MortgageType: null,
  AssociatedAssetId: null,
  Contributions: [getDefaultContribution()],
  TaxStatus: null,
  OrionManagementStyleId: null,
  IsBypassRothIraIncomeLimits: false,
  StudentLoanOriginationDate: null,
  OwnerModel: [],
};

export interface FormComponentProps {
  accountType: AccountType | null;
  defaultSelectedTab?: string;
  errorMessage?: string;
  accountId?: string;
}

export interface TabComponentProps {
  accountType: AccountType | null;
  accountId: string;
}

export const OWNERTYPE_MAP = {
  [OwnerTypeDTO.Principal]: 'Principal',
  [OwnerTypeDTO.Spouse]: 'Spouse',
  [OwnerTypeDTO.Joint]: 'Joint',
  [OwnerTypeDTO.Trust]: 'Trust',
  [OwnerTypeDTO.Business]: 'Business',
  [OwnerTypeDTO.Child]: 'Child',
  [OwnerTypeDTO.Grandchild]: 'Grandchild',
  [OwnerTypeDTO.OtherDependent]: 'Other Dependent',
  [OwnerTypeDTO.OtherBeneficiary]: 'Other Beneficiary',
  [OwnerTypeDTO.OtherIndividual]: 'Other Individual',
};

interface GetOwnerFullNamesFromOptionsParams {
  ownerModel: OwnerModelFormDTO[] | undefined;
  people: OwnerOptionDTO[];
  beneficiariesOptions: BeneficiaryOptionDTO[];
}

const getOwnerFullNamesFromOptions = ({
  ownerModel,
  people,
  beneficiariesOptions,
}: GetOwnerFullNamesFromOptionsParams) => {
  if (!ownerModel?.length || !people?.length) return [];
  const names = new Set<string>();
  const beneficiariesOpts = beneficiariesOptions ?? [];
  for (const owner of ownerModel.filter(
    o => o.OwnerType === OwnerTypeDTO.Principal || o.OwnerType === OwnerTypeDTO.Spouse,
  )) {
    const peopleMatch = owner.Id
      ? people.find(p => p.Id === owner.Id)
      : people.find(p => p.OwnerType === owner.OwnerType);
    const ownerName = peopleMatch?.Name ?? owner.Name;
    if (!ownerName) continue;
    const fullNameMatch = beneficiariesOpts.find(
      b =>
        b.Name &&
        (b.Name === ownerName ||
          b.Name.toLowerCase().startsWith(ownerName.toLowerCase().trim() + ' ')),
    );
    const fullName = fullNameMatch?.Name ?? ownerName;
    names.add(fullName.toLowerCase().trim());
  }
  return Array.from(names);
};

const positionsValidation = yup.object().shape({
  Name: yup
    .string()
    .nullable()
    .required('Name is required'),
  Ticker: yup
    .string()
    .nullable()
    .required('Ticker is required'),
  Quantity: yup
    .string()
    .nullable()
    .required('Quantity is required'),
  Price: yup
    .string()
    .nullable()
    .required('Price is required'),
  AssetBucketIndex: yup
    .number()
    .when(['$allowAssetBucketingCustomization', '$assetBucketingByManagementStyle'], {
      is: (allowAssetBucketingCustomization: boolean, assetBucketingByManagementStyle: boolean) => {
        return !assetBucketingByManagementStyle && allowAssetBucketingCustomization;
      },
      then: yup
        .number()
        .nullable()
        .required('This field is required'),
    })
    .nullable(),
});

export const validation = (options: OptionsDTO) =>
  yup.object().shape({
    bankAccount: yup.object().shape({
      Positions: yup.array().when(['IsLinked', 'IsIntegrated'], {
        is: (IsLinked: boolean, IsIntegrated: boolean) => {
          return !(IsLinked || IsIntegrated);
        },
        then: yup.array().of(positionsValidation),
        otherwise: yup.array().notRequired(),
      }),
      OwnerModel: yup
        .array()
        .test(
          'check-total',
          'The total ownership percentage must add up to 100%',
          (OwnerModel, ctx) => {
            if (!OwnerModel || !Array.isArray(OwnerModel)) return false;
            if (OwnerModel[0].Name === '') {
              return ctx.createError({
                path: 'bankAccount.OwnerModel[0].Id',
                message: 'Please specify an account owner',
              });
            }
            const sum = OwnerModel.reduce((acc, obj) => {
              if (obj.Name && obj.Percentage) {
                return acc + (parseFloat(obj.Percentage) || 0);
              }
              return acc;
            }, 0);
            if (sum !== 100) {
              return ctx.createError({
                path: 'bankAccount.OwnerModel[0].Id',
                message: 'The total ownership percentage must add up to 100%',
              });
            }
            return sum === 100;
          },
        ),
      AccountBeneficiaries: yup
        .object()
        .nullable()
        .shape({
          Beneficiaries: yup.array(),
        })
        .test(
          'primary-required-if-contingent',
          'You must have a primary beneficiary if you are adding a contingent beneficiary',
          function(accountBeneficiaries) {
            const { AccountType: accountType } = this.parent || {};
            if (!ACCOUNTS_WITH_BENEFICIARIES_TAB.includes(accountType)) {
              return true;
            }
            const beneficiaries = accountBeneficiaries?.Beneficiaries;
            if (!beneficiaries || beneficiaries.length === 0) return true;
            const hasContingent = beneficiaries.some(
              (b: { Type?: number }) => b.Type === BeneficiaryType.Contingent,
            );
            const hasPrimary = beneficiaries.some(
              (b: { Type?: number }) => b.Type === BeneficiaryType.Primary,
            );
            if (hasContingent && !hasPrimary) return false;
            return true;
          },
        )
        .test(
          'beneficiaries-total-100',
          'The total of beneficiaries percentages added must add up to 100%',
          function(accountBeneficiaries) {
            const { AccountType: accountType } = this.parent || {};
            if (!ACCOUNTS_WITH_BENEFICIARIES_TAB.includes(accountType)) {
              return true;
            }
            const beneficiaries = accountBeneficiaries?.Beneficiaries;
            if (!beneficiaries || beneficiaries.length === 0) return true;
            const beneficiariesWithName = beneficiaries.filter(
              (b: { Name?: string }) => b.Name && String(b.Name).trim(),
            );
            if (beneficiariesWithName.length === 0) return true;
            const primary = beneficiariesWithName.filter(
              (b: { Type?: number }) => b.Type === BeneficiaryType.Primary,
            );
            const contingent = beneficiariesWithName.filter(
              (b: { Type?: number }) => b.Type === BeneficiaryType.Contingent,
            );
            const primarySum = primary.reduce(
              (acc: number, b: { Percentage?: string | number }) => acc + Number(b.Percentage || 0),
              0,
            );
            const contingentSum = contingent.reduce(
              (acc: number, b: { Percentage?: string | number }) => acc + Number(b.Percentage || 0),
              0,
            );
            const primaryValid = primary.length === 0 || primarySum === 100;
            const contingentValid = contingent.length === 0 || contingentSum === 100;
            return primaryValid && contingentValid;
          },
        )
        .test(
          'owner-cannot-be-beneficiary',
          'The account owner cannot be added as a primary or contingent beneficiary',
          function(accountBeneficiaries) {
            const { AccountType: accountType, OwnerModel: ownerModel } = this.parent || {};
            if (!ACCOUNTS_WITH_BENEFICIARIES_TAB.includes(accountType)) {
              return true;
            }
            const beneficiaries = accountBeneficiaries?.Beneficiaries;
            if (!beneficiaries?.length) return true;
            if (!ownerModel?.length) return true;
            const people = options.IndividualOwnerOptions?.People ?? [];
            const beneficiariesOptions = options.BeneficiariesOptions ?? [];
            const ownerFullNames = getOwnerFullNamesFromOptions({
              ownerModel,
              people,
              beneficiariesOptions,
            });

            if (ownerFullNames.length === 0) return true;
            const hasOwnerAsBeneficiary = beneficiaries.some((b: { Name?: string }) => {
              if (!b.Name) return false;
              const beneficiaryName = b.Name.toLowerCase().trim();
              return ownerFullNames.some(ownerFullName => beneficiaryName === ownerFullName);
            });
            return !hasOwnerAsBeneficiary;
          },
        ),
    }),
    accountPortfolio: yup
      .object()
      .nullable()
      .default(null)
      .shape({
        assetAllocationList: yup.array().of(
          yup.object().when('$showHypotheticalReturn', {
            is: true,
            then: yup.object().shape({
              hypotheticalReturn: hypotheticalReturnValidation,
            }),
            otherwise: yup.object().shape({}),
          }),
        ),
      }),
  });
